/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useState, useRef } from 'react';
import { useElevenLabsContext } from '../../contexts/ElevenLabsContext';
import './VoiceCloning.scss';

export interface VoiceCloningProps {
  onVoiceCreated?: (voiceId: string) => void;
  className?: string;
}

export const VoiceCloning: React.FC<VoiceCloningProps> = ({
  onVoiceCreated,
  className = '',
}) => {
  const { cloneVoice, isLoading, error, clearError } = useElevenLabsContext();
  
  const [voiceName, setVoiceName] = useState('');
  const [voiceDescription, setVoiceDescription] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file selection
  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;
    
    const audioFiles = Array.from(files).filter(file => 
      file.type.startsWith('audio/') && 
      file.size <= 50 * 1024 * 1024 // 50MB limit
    );
    
    setSelectedFiles(prev => [...prev, ...audioFiles]);
  };

  // Handle drag events
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  // Remove file
  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Handle voice cloning
  const handleCloneVoice = async () => {
    if (!voiceName.trim() || selectedFiles.length === 0) {
      return;
    }

    setIsUploading(true);
    setProgress(0);

    try {
      const voiceId = await cloneVoice({
        name: voiceName,
        description: voiceDescription,
        files: selectedFiles,
      });
      
      setProgress(100);
      onVoiceCreated?.(voiceId);
      
      // Reset form
      setVoiceName('');
      setVoiceDescription('');
      setSelectedFiles([]);
      
    } catch (err) {
      console.error('Voice cloning error:', err);
    } finally {
      setIsUploading(false);
      setProgress(0);
    }
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isValid = voiceName.trim() && selectedFiles.length > 0 && !isLoading && !isUploading;

  return (
    <div className={`voice-cloning ${className}`}>
      <h2>Create Custom Voice</h2>
      <p className="voice-cloning__description">
        Clone your voice by uploading audio samples. We recommend 3-10 minutes of clean audio recordings.
      </p>

      {/* Error Display */}
      {error && (
        <div className="voice-cloning__error">
          <span>{error}</span>
          <button onClick={clearError} className="voice-cloning__error-close">
            ×
          </button>
        </div>
      )}

      {/* Voice Details Form */}
      <div className="voice-cloning__form">
        <div className="voice-cloning__field">
          <label htmlFor="voice-name" className="voice-cloning__label">
            Voice Name *
          </label>
          <input
            id="voice-name"
            type="text"
            value={voiceName}
            onChange={(e) => setVoiceName(e.target.value)}
            placeholder="My Custom Voice"
            className="voice-cloning__input"
            disabled={isLoading || isUploading}
            maxLength={100}
          />
        </div>

        <div className="voice-cloning__field">
          <label htmlFor="voice-description" className="voice-cloning__label">
            Description
          </label>
          <textarea
            id="voice-description"
            value={voiceDescription}
            onChange={(e) => setVoiceDescription(e.target.value)}
            placeholder="Describe this voice (optional)"
            className="voice-cloning__textarea"
            disabled={isLoading || isUploading}
            rows={3}
            maxLength={500}
          />
        </div>
      </div>

      {/* File Upload Area */}
      <div className="voice-cloning__upload">
        <div
          className={`voice-cloning__drop-zone ${isDragging ? 'voice-cloning__drop-zone--dragging' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="voice-cloning__upload-icon">📁</div>
          <p className="voice-cloning__upload-text">
            Drag and drop audio files here, or click to select
          </p>
          <p className="voice-cloning__upload-hint">
            Supported formats: MP3, WAV, M4A (Max 50MB per file)
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="audio/*"
            onChange={(e) => handleFileSelect(e.target.files)}
            className="voice-cloning__file-input"
            disabled={isLoading || isUploading}
          />
        </div>

        {/* File List */}
        {selectedFiles.length > 0 && (
          <div className="voice-cloning__file-list">
            <h4>Selected Files ({selectedFiles.length})</h4>
            {selectedFiles.map((file, index) => (
              <div key={index} className="voice-cloning__file-item">
                <div className="voice-cloning__file-info">
                  <span className="voice-cloning__file-name">{file.name}</span>
                  <span className="voice-cloning__file-size">{formatFileSize(file.size)}</span>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="voice-cloning__file-remove"
                  disabled={isLoading || isUploading}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Progress Bar */}
      {isUploading && (
        <div className="voice-cloning__progress">
          <div className="voice-cloning__progress-bar">
            <div
              className="voice-cloning__progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="voice-cloning__progress-text">
            Creating voice... {progress}%
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="voice-cloning__actions">
        <button
          onClick={handleCloneVoice}
          disabled={!isValid}
          className="voice-cloning__button voice-cloning__button--primary"
        >
          {isUploading ? 'Creating Voice...' : 'Create Voice'}
        </button>
        
        <button
          onClick={() => {
            setVoiceName('');
            setVoiceDescription('');
            setSelectedFiles([]);
          }}
          disabled={isLoading || isUploading}
          className="voice-cloning__button voice-cloning__button--secondary"
        >
          Clear
        </button>
      </div>

      {/* Guidelines */}
      <div className="voice-cloning__guidelines">
        <h4>Voice Cloning Guidelines</h4>
        <ul>
          <li>Upload 3-10 minutes of clean audio recordings</li>
          <li>Use consistent tone and speaking style</li>
          <li>Record in a quiet environment with minimal background noise</li>
          <li>Speak clearly and naturally</li>
          <li>Each file should be less than 50MB</li>
          <li>Supported formats: MP3, WAV, M4A</li>
        </ul>
      </div>
    </div>
  );
};

export default VoiceCloning;
